import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import HomeView from "./views/HomeView.vue";
import CreateView from "./views/CreateView.vue";
import RoomView from "./views/RoomView.vue";
import RecordView from "./views/RecordView.vue";
import AllRooms from "./views/AllRooms.vue";
import ErrorView from "./views/ErrorView.vue";
import ButtonCreate from "./views/ButtonCreate.vue";
import {
  authErrorState,
  createNewUser,
  isAuth,
  login,
  login_recorder,
  setLocalLogin,
} from "./states/auth";
import { loadPage } from "./states/loadState";
import { domain_once } from "./states/domains";
import ChatHistory from "./views/ChatHistory.vue";
import { goToWait } from "./states/toWaitState";
import AdminLogin from "./views/admin/AdminLogin.vue";
import AdminAiSettings from "./views/admin/AdminAiSettings.vue";
import { checkSuperAdmin, redirectAfterLogin } from "./api/superadmin";
import AdminAccounts from "./views/admin/AdminAccounts.vue";
import AdminAiAccess from "./views/admin/AdminAiAccess.vue";
import AdminAccessRequests from "./views/admin/AdminAccessRequests.vue";
import SummaryView from "./views/SummaryView.vue";
import { waitSegmenter } from "./utils/segmenter";
import { loadAudioModules } from "./states/audioCtx";

type RouteType = RouteRecordRaw & { name: string };

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
  },
  {
    path: "/error/:errtype",
    name: "error",
    component: ErrorView,
    props: true,
  },
  {
    path: "/admin/login",
    name: "admin-login",
    alias: "/admin",
    component: AdminLogin,
  },
  {
    path: "/admin/ai-settings",
    name: "admin-ai-settings",
    component: AdminAiSettings,
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/ai-access",
    name: "admin-ai-access",
    component: AdminAiAccess,
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/external-users",
    name: "admin-external-users",
    component: () => import("./views/admin/AdminExternalUsers.vue"),
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/accounts",
    name: "admin-accounts",
    component: AdminAccounts,
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/access-requests",
    name: "admin-access-requests",
    component: AdminAccessRequests,
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/roles",
    name: "admin-roles",
    component: () => import("./views/admin/AdminRoles.vue"),
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/statistics",
    name: "admin-statistics",
    component: () => import("./views/admin/AdminStatistics.vue"),
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/calls-report",
    name: "admin-calls-report",
    component: () => import("./views/admin/AdminCallsReport.vue"),
    meta: {
      superAdmin: true,
      keep: true,
    },
  },
  {
    path: "/admin/call-report/:callId",
    name: "admin-call-report",
    component: () => import("./views/admin/AdminCallReport.vue"),
    props: true,
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/admin/no-access",
    name: "admin-no-access",
    component: () => import("./views/admin/AdminNoAccess.vue"),
    meta: {
      superAdmin: true,
    },
  },
  {
    path: "/button-create",
    name: "btn-create",
    component: ButtonCreate,
  },
  {
    path: "/create",
    name: "create",
    component: CreateView,
  },
  {
    path: "/rooms",
    name: "rooms",
    component: AllRooms,
  },
  {
    path: "/record/:roomId",
    name: "room-record",
    component: RecordView,
    props: true,
  },
  {
    path: "/chat-history/:roomId",
    name: "chat-history",
    props: true,
    component: ChatHistory,
  },
  {
    path: "/s/:summaryId",
    name: "summary-view",
    component: SummaryView,
    props: true,
  },
  {
    path: "/:roomId",
    name: "room",
    component: RoomView,
    props: true,
  },
] as const satisfies readonly RouteType[];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

export type VCSRouteName = (typeof routes)[number]["name"];

router.beforeEach(async (to) => {
  waitSegmenter()
  const settings = domain_once();
  try {
    if (settings !== true) await settings;
  } catch {
    return {
      name: "error",
      params: {
        errtype: "unknown-error",
      },
    };
  }
  if(!(to.name === 'room-record' || to.meta?.superAdmin)) {
    await loadAudioModules()
  }
  if (to.meta?.superAdmin) {
    const res = await checkSuperAdmin();
    if (res) {
      return true;
    } else {
      return {
        name: "admin-login",
      };
    }
  }
  if (to.name === "admin-login") {
    const res = await redirectAfterLogin();
    if (res === "admin-login") {
      return true;
    } else {
      return {
        name: res,
      };
    }
  }
  if (authErrorState.value) {
    if (to.name === "error" && to.params.errtype === authErrorState.value) {
      return true;
    } else {
      return {
        name: "error",
        params: {
          errtype: authErrorState.value,
        },
      };
    }
  }
  if (to.name === "room-record") {
    if (!isAuth()) {
      if (to.query.creds) {
        try {
          await login_recorder(
            to.query.creds as string,
            to.params.roomId as string,
          );
        } catch {
          return {
            name: "error",
            params: {
              errtype: "bad-user",
            },
          };
        }
      } else {
        return {
          name: "error",
          params: {
            errtype: "bad-user",
          },
        };
      }
    }
  } else if (to.name === "error") {
    return true;
  } else if (to.name === "home") {
    if (to.query.token) {
      try {
        await login(to.query.token as string);
        return {
          name: "create",
        };
      } catch (err: any) {
        let err_msg = err?.data?.message || err?.data?.error;
        if (err_msg === "another auth call exist") {
          return {
            name: "error",
            params: {
              errtype: "call-exist",
            },
          };
        } else if (err_msg === "Token expired") {
          return {
            name: "error",
            params: {
              errtype: "token-expired",
            },
          };
        }
        return {
          name: "error",
          params: {
            errtype: "miss-connect-login",
          },
        };
      }
    } else {
      //for dev
      if (import.meta.env.DEV) {
        try {
          await setLocalLogin();
          return {
            name: "rooms",
          };
        } catch {
          await createNewUser();
          return {
            name: "rooms",
          };
        }
      } else {
        return {
          name: "error",
          params: {
            errtype: "bad-query",
          },
        };
        // redirect to main page
      }
    }
  } else if (to.name === "create" && !isAuth()) {
    return {
      name: "home",
    };
  } else if (to.name === "room") {
    if (to.query.wait) {
      goToWait.value = true;
    }
    if (to.query.token) {
      try {
        await login(to.query.token as string);
        return {
          name: "room",
          params: to.params,
        };
      } catch (err: any) {
        let err_msg = err?.data?.message || err?.data?.error;
        if (err_msg === "another auth call exist") {
          return {
            name: "error",
            params: {
              errtype: "call-exist",
            },
          };
        } else if (err_msg === "Token expired") {
          return {
            name: "error",
            params: {
              errtype: "token-expired",
            },
          };
        }
        return {
          name: "error",
          params: {
            errtype: "miss-connect-login",
          },
        };
      }
    } else if (!isAuth()) {
      try {
        await setLocalLogin();
      } catch {
        try {
          await createNewUser();
        } catch {
          return {
            name: "error",
            params: {
              errtype: "server-error",
            },
          };
        }
      }
    }
  } else if (to.name === "chat-history") {
    if (isAuth()) return true;
    else {
      try {
        await setLocalLogin();
        return true;
      } catch {
        return {
          name: "error",
          params: {
            errtype: "bad-user",
          },
        };
      }
    }
  } else {
    return true;
  }
});

router.afterEach(() => {
  loadPage.value = false;
});
